import { Inject, Injectable, Optional, PLATFORM_ID, signal } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter } from 'rxjs';
import { User } from '../models/user.model';
import { AuthPolicy } from '../enums/auth-policy';
import { PolicyService } from './policy.service';
import { AuthRole } from '../enums/auth-role';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  userDetails = signal<User>({
    username: null,
    email: null,
    roles: [],
  });
  roles: string[] = [];
  isAuthenticated = signal<boolean>(false);

  constructor(@Optional() @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration | null,
    @Optional() private msalService: MsalService,
    @Optional() private msalBroadcastService: MsalBroadcastService,
    private policyService: PolicyService,
    @Inject(PLATFORM_ID) private platformId: Object) {

    if (!isPlatformBrowser(this.platformId) || !this.msalService || !this.msalBroadcastService) {
      return;
    }

    this.setIsAuthenticated();
    this.checkAndSetActiveAccount();
    this.updateUserDetails();

    this.msalService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setIsAuthenticated();
        this.checkAndSetActiveAccount();
        this.updateUserDetails();
      });
  }

  public isPolicy(policy: AuthPolicy) {
    return this.policyService.getPolicy(policy, this.roles);
  }

  public isPolicyUser() {
    return this.policyService.getPolicy(AuthPolicy.User, this.roles);
  }

  public isPolicyPT() {
    return this.policyService.getPolicy(AuthPolicy.PT, this.roles);
  }

  public isPolicyPTAdmin() {
    return this.policyService.getPolicy(AuthPolicy.PTAdmin, this.roles);
  }

  public isPolicyDeveloper() {
    return this.policyService.getPolicy(AuthPolicy.Developer, this.roles);
  }

  private setIsAuthenticated() {
    if (!this.msalService) {
      this.isAuthenticated.set(false);
      return;
    }

    this.isAuthenticated.set(this.msalService.instance.getAllAccounts().length > 0);
  }

  private checkAndSetActiveAccount() {
    if (!this.msalService) {
      return;
    }

    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private updateUserDetails() {
    if (!this.msalService) {
      return;
    }

    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      const username = account.name || null;
      const email = account.idTokenClaims?.preferred_username || null;
      const roles = account.idTokenClaims?.roles || [];

      this.userDetails.set({ username, email, roles });

      this.roles = account.idTokenClaims?.roles || [];

      if (!this.isPolicyUser()) {
        this.logout();
      }
    }
  }

  logout() {
    if (!this.msalService || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const activeAccount = this.msalService.instance.getActiveAccount();

    if (activeAccount) {
      this.msalService.logoutRedirect({
        account: activeAccount
      });
    } else {
      this.msalService.logoutRedirect();
    }
  }
}
